import { AlertCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/glitch-ui/spinner';
import { useDelayedFlag } from '@/hooks/useDelayedFlag';

/** How long a page load may run before the spinner shows, so a fast or cached load never flashes it. */
export const PAGE_LOADING_DELAY_MS = 200;

interface EmptyStateProps {
  icon?: React.ReactNode;
  title: string;
  description: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
  /**
   * The hint form, for a side panel beside a table (#6724): it must not set
   * the card's height, or a table with one row leaves a tall empty stretch
   * beside a tall placeholder. It does fill whatever height its panel gives
   * it and centre itself in that (#6743), capped at a screen so a hint
   * beside a 150-row table centres on the screen rather than thousands of
   * pixels down. The panel is responsible for taking it out of flow, so a
   * short table is never stretched to fit it. A page's own empty state keeps
   * the full breathing room; PageEmptyState ignores this.
   */
  compact?: boolean;
}

/**
 * EmptyState - Inline empty state for sections, panels, tabs, modals
 *
 * Passive states (no action): All text is gray-500
 * Active states (with action): All text is gray-900
 *
 * For page-level empty states, use PageEmptyState instead.
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
  className = '',
  compact = false,
}: EmptyStateProps) {
  // Use gray-500 for passive states (no action), gray-900 for active states (has action)
  const textColor = action ? 'text-gray-900' : 'text-gray-500';

  return (
    // h-full, never min-h: the panel decides how tall it is, and the hint
    // centres inside that rather than forcing a height of its own.
    <Card className={`rounded-lg border bg-white ${compact ? 'flex h-full max-h-[calc(100vh-10rem)] items-center justify-center' : ''} ${className}`}>
      <CardContent className={compact ? 'w-full p-0' : 'p-6'}>
        <div className={`flex flex-col items-center justify-center text-center ${compact ? 'py-2' : 'py-8'}`}>
          {icon && <div className={compact ? 'mb-2' : 'mb-4'}>{icon}</div>}
          <h3 className={`font-semibold ${textColor} mb-2 ${compact ? 'text-base' : 'text-lg'}`}>{title}</h3>
          <p className={`${textColor}${action ? ' mb-4' : ''}${compact ? ' text-sm' : ''}`}>{description}</p>
          {action}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * PageEmptyState - Full-page empty state with centered content
 *
 * Use this for page-level empty states where the entire page has no content.
 * Centers content vertically with min-height of 66vh.
 *
 * Passive states (no action): All text is gray-500
 * Active states (with action): All text is gray-900
 */
export function PageEmptyState({
  icon,
  title,
  description,
  action,
  className = '',
}: EmptyStateProps) {
  // Use gray-500 for passive states (no action), gray-900 for active states (has action)
  const textColor = action ? 'text-gray-900' : 'text-gray-500';

  return (
    <Card className={`rounded-lg border bg-white min-h-[66vh] flex items-center justify-center ${className}`}>
      <CardContent className="p-6 w-full">
        <div className="flex flex-col items-center justify-center py-8 text-center">
          {icon && <div className="mb-4">{icon}</div>}
          <h3 className={`text-lg font-semibold ${textColor} mb-2`}>{title}</h3>
          <p className={`${textColor}${action ? ' mb-4' : ''}`}>{description}</p>
          {action}
        </div>
      </CardContent>
    </Card>
  );
}

interface PageErrorStateProps {
  title?: string;
  description?: React.ReactNode;
  onRetry?: () => void;
  retryLabel?: string;
  className?: string;
}

/**
 * PageErrorState - Full-page load-failure state.
 *
 * Use when a page's primary data fails to load (4xx/5xx). This is deliberately
 * distinct from PageEmptyState ("nothing here yet") so a load error never poses
 * as missing data, which would mislead the user into thinking they have no
 * campaigns/business/reports when the request actually failed.
 *
 * Red AlertCircle (semantic error #B91C1C) + optional Retry button. Wraps
 * PageEmptyState so layout and spacing stay identical to the empty state.
 */
export function PageErrorState({
  title = 'Something Went Wrong',
  description = 'This page failed to load. This is a loading error, not missing data, so nothing has been lost. Retry, or contact support if it persists.',
  onRetry,
  retryLabel = 'RETRY',
  className = '',
}: PageErrorStateProps) {
  return (
    <PageEmptyState
      className={className}
      icon={<AlertCircle className="h-12 w-12 text-[#B91C1C]" strokeWidth={1} />}
      title={title}
      description={description}
      action={
        onRetry ? (
          <Button
            onClick={onRetry}
            size="lg"
            className="w-[280px] py-6 bg-[#290BD9] hover:bg-[#2309C5] text-white shadow-lg flex items-center justify-center space-x-2"
          >
            <span>{retryLabel}</span>
          </Button>
        ) : undefined
      }
    />
  );
}

/**
 * PageLoadingState - The page-level loading state (#6780).
 *
 * Render it in place of a page's content while its primary data loads or while it redirects, and as a
 * route's `pendingComponent`; a page never renders nothing while it waits. A centred `xl` cobalt
 * Spinner that appears only after PAGE_LOADING_DELAY_MS; until then the box holds its height empty, so
 * a fast load shows no spinner and the layout does not jump when it appears.
 */
export function PageLoadingState() {
  const showSpinner = useDelayedFlag(true, PAGE_LOADING_DELAY_MS);

  return (
    <div className="flex h-64 items-center justify-center">
      {showSpinner && <Spinner size="xl" className="text-[#290BD9]" />}
    </div>
  );
}
